/** Lektions-Multi-Select fuer den Composer (Scope-Auswahl, DD1/DD6). */
import { useState, useRef, useEffect } from 'react'
import { ChevronDown, Check } from 'lucide-react'
import type { ScopeLessonInfo } from '@/types/teacher-chat'
import { lessonShortLabel } from './scopeUtils'

interface ScopeSelectorProps {
  lessons: ScopeLessonInfo[]
  selectedIds: string[]
  onChange: (ids: string[]) => void
  disabled?: boolean
}

export function ScopeSelector({ lessons, selectedIds, onChange, disabled }: ScopeSelectorProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  if (lessons.length === 0) return null

  const toggle = (id: string) => {
    if (selectedIds.includes(id)) onChange(selectedIds.filter(x => x !== id))
    else onChange([...selectedIds, id])
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        disabled={disabled}
        className="px-2.5 py-1.5 text-xs text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 disabled:opacity-50 flex items-center gap-1 bg-white shrink-0"
        aria-label="Lektionen auswählen"
      >
        <span>{selectedIds.length ? `${selectedIds.length} Lektion${selectedIds.length !== 1 ? 'en' : ''}` : 'Lektion'}</span>
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute bottom-full mb-1 left-0 bg-white border border-gray-200 rounded-lg shadow-lg z-20 min-w-[260px] max-h-72 overflow-y-auto">
          <div className="px-3 py-2 text-[10px] text-gray-400 font-medium uppercase tracking-wide border-b border-gray-100 sticky top-0 bg-white">
            Betrifft Lektionen
          </div>
          {lessons.map(l => {
            const checked = selectedIds.includes(l.lesson_request_id)
            return (
              <label
                key={l.lesson_request_id}
                className="flex items-start gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-xs"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(l.lesson_request_id)}
                  className="mt-0.5 accent-indigo-600"
                />
                <span className="text-gray-700 leading-snug">
                  {lessonShortLabel(l)}
                  {l.room_name ? ` · Raum ${l.room_name}` : ''}
                </span>
                {checked && <Check className="w-3 h-3 text-indigo-600 ml-auto shrink-0 mt-0.5" />}
              </label>
            )
          })}
          {selectedIds.length > 0 && (
            <button
              type="button"
              onClick={() => { onChange([]); setOpen(false) }}
              className="w-full text-left px-3 py-2 text-xs text-gray-500 hover:bg-gray-50 border-t border-gray-100"
            >
              Auswahl zurücksetzen
            </button>
          )}
        </div>
      )}
    </div>
  )
}
